/**
 * Phaser 背景レイヤの React ラッパ。
 *
 * Phaser 本体は **ここで初めて dynamic import される**(初期バンドルに載せない)。
 * 読み込み前・WebGL不可の環境では CSS グラデーションのままなので、演出が無いだけで遊べる。
 */

import type { PerformanceStage } from '@paltyping/engine';
import { useEffect, useRef } from 'react';
import type { BackgroundHandle } from '../game/background';
import type { Emitter } from '../bridge/emitter';
import type { PlayEffect } from '../bridge/playController';

interface Props {
  readonly stage: PerformanceStage;
  readonly effects: Emitter<PlayEffect>;
}

export function PhaserBackground({ stage, effects }: Props) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const handleRef = useRef<BackgroundHandle | null>(null);
  const stageRef = useRef<PerformanceStage>(stage);
  stageRef.current = stage;

  useEffect(() => {
    let disposed = false;
    const host = hostRef.current;
    if (!host) return;

    void import('../game/background')
      .then(({ createBackground }) => {
        if (disposed) return;
        handleRef.current = createBackground(host, stageRef.current);
      })
      .catch(() => {
        // Phaser が読めない/WebGLが無い環境では背景演出を諦める(プレイは継続)。
      });

    return () => {
      disposed = true;
      handleRef.current?.destroy();
      handleRef.current = null;
    };
  }, []);

  useEffect(() => {
    handleRef.current?.setStage(stage);
  }, [stage]);

  useEffect(
    () =>
      effects.on((effect) => {
        const handle = handleRef.current;
        if (!handle) return;
        if (effect.type === 'correct') handle.pulse('correct');
        else if (effect.type === 'miss') handle.pulse('miss');
        else if (effect.type === 'wordCompleted') handle.pulse('word');
      }),
    [effects],
  );

  return <div className={`phaser-layer stage-${stage}`} ref={hostRef} aria-hidden="true" />;
}
